import { inject, Injectable, signal } from '@angular/core';
import { AlertStatus } from '@features/dashboard/interfaces/alert-status';
import { Alert } from '@interfaces/alert/alert';
import { AlertCloseRequest } from '@interfaces/alert/alert-close-request';
import { AlertTime } from '@interfaces/alert/alert-time';
import { Recipient } from '@interfaces/alert/recipient';
import { v4 as uuid } from 'uuid';
import { TopicType } from '../enums/topic-type';
import { AuthService } from './auth.service';
import { WebSocketService } from './web-socket.service';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private authService = inject(AuthService);
  private webSocketService = inject(WebSocketService);
  alerts = signal<Alert[]>([]);

  readAlerts() {
    const updatedAlerts = this.alerts().map((a) => ({
      ...a,
      ui: { ...a.ui, isRead: false },
    }));
    this.alerts.set(updatedAlerts);
  }

  addAlert(alert: Alert) {
    alert.ui = {
      isCollapsed: false,
      isCleared: false,
      isClosed: false,
      isActive: false,
      isRead: false,
      startTime: { minutes: 0, seconds: 0 },
    };

    const mappedAlert = this.mapAlert(alert, alert);
    this.alerts.update((prevAlerts) => [mappedAlert, ...prevAlerts]);
  }

  removeAlertFromList(alert: Alert) {
    this.alerts.update((prevState) =>
      prevState.filter((item) => item.sequence !== alert.sequence),
    );
  }

  sendCloseAlertRequest(alert: Alert) {
    const requester = this.authService.user().id!;
    const message: AlertCloseRequest = {
      requester,
      alertId: `@${alert.sequence}`,
    };
    this.webSocketService.send({
      topic: TopicType.CLOSE_ALERT_REQUEST,
      message,
    });
  }

  sendAlertNotificationRequest(message: object) {
    this.webSocketService.send({
      topic: TopicType.ALERT_NOTIFICATION_REQUEST,
      message,
      regenerateUuid: true,
    });
  }

  updateAlert(updatedAlert: Alert) {
    this.alerts.update((prevAlerts) => {
      return prevAlerts.map((alert) => {
        return this.mapAlert(alert, updatedAlert);
      });
    });
  }

  private mapAlert(alert: Alert, updatedAlert: Alert) {
    if (alert.sequence === updatedAlert.sequence) {
      if (AlertStatus.CLEARED === updatedAlert.status) {
        alert.ui.isCleared = true;
      } else if (AlertStatus.CLOSED === updatedAlert.status) {
        alert.ui.isClosed = true;
      }

      for (let i = 0; i < alert.recipients?.length!; i++) {
        if (alert.recipients && alert.recipients[i]) {
          const existingRes = alert.recipients[i];
          if (existingRes) {
            const foundRes = updatedAlert.responses?.find(
              (res) => res.user.id === existingRes.id,
            );
            if (foundRes) {
              alert.recipients[i] = {
                ...alert.recipients[i],
                status: foundRes.responseStatus,
              };
            }
          }
        }
      }
      if (alert.recipients) {
        alert.ui.isActive =
          alert.recipients!.filter((res) => res?.status?.includes('accepted'))
            ?.length > 0;
      }
    }
    return alert;
  }

  checkCollapsedAlerts() {
    let minimalizedAlerts = sessionStorage.getItem('alerts');
    let minimalizedAlertsArray: string[] = minimalizedAlerts
      ? JSON.parse(minimalizedAlerts)
      : [];

    this.alerts.update((prevAlerts) => {
      prevAlerts = prevAlerts.map((alert) => {
        if (minimalizedAlertsArray.includes(alert.sequence)) {
          alert.ui.isCollapsed = true;
        }

        return alert;
      });
      return prevAlerts;
    });
  }

  collapseAlert(collapsedAlert: Alert) {
    this.alerts.update((prevAlerts) => {
      prevAlerts = prevAlerts.map((alert) => {
        if (alert.key === collapsedAlert.key) {
          alert.ui.isCollapsed = true;
          alert.ui.startTime = collapsedAlert.ui.startTime;
          let minimalizedAlerts = sessionStorage.getItem('alerts');
          let minimalizedAlertsArray: string[] = minimalizedAlerts
            ? JSON.parse(minimalizedAlerts)
            : [];

          minimalizedAlertsArray.push(alert.sequence);

          sessionStorage.setItem(
            'alerts',
            JSON.stringify(minimalizedAlertsArray),
          );
        }
        return alert;
      });
      return prevAlerts;
    });
  }

  expandAlert(expandedAlert: Alert) {
    this.alerts.update((prevAlerts) => {
      prevAlerts = prevAlerts.map((alert) => {
        if (alert.key === expandedAlert.key) {
          alert.ui.isCollapsed = false;

          let minimalizedAlerts = sessionStorage.getItem('alerts');
          let minimalizedAlertsArray: string[] = minimalizedAlerts
            ? JSON.parse(minimalizedAlerts)
            : [];

          minimalizedAlertsArray = minimalizedAlertsArray.filter(
            (item: string) => item !== alert.sequence,
          );

          sessionStorage.setItem(
            'alerts',
            JSON.stringify(minimalizedAlertsArray),
          );

          alert.ui.startTime = expandedAlert.ui.startTime;
        }
        return alert;
      });
      return prevAlerts;
    });
  }

  setFrozenTime(modifiedAlert: Alert, frozenTime: AlertTime) {
    this.alerts.update((prevAlerts) => {
      prevAlerts = prevAlerts.map((alert) => {
        if (alert.key === modifiedAlert.key) {
          alert.ui.frozenTime = frozenTime;
        }
        return alert;
      });
      return prevAlerts;
    });
  }

  setAlerts(alerts: Alert[]) {
    this.alerts.set([]);
    for (const alert of alerts) {
      const recipients: Recipient[] | undefined = alert.responses?.map(
        (res) => {
          return { ...res.user, status: res.responseStatus };
        },
      );
      if (recipients) {
        alert.recipients = recipients;
      }
      alert.key = uuid();
      this.addAlert(alert);
    }
  }

  minimizeAllAlerts(): void {
    this.alerts.update((prevAlerts) => {
      const minimizedSequences: string[] = [];

      const updated = prevAlerts.map((alert) => {
        alert.ui.isCollapsed = true;
        minimizedSequences.push(alert.sequence);
        return alert;
      });

      sessionStorage.setItem('alerts', JSON.stringify(minimizedSequences));

      return updated;
    });
  }

  hasExpandedAlerts(): boolean {
    return this.alerts().some((alert) => !alert.ui.isCollapsed);
  }
}
